import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

interface HowToStep {
  name: string;
  text: string;
}

interface HowToSchema {
  name: string;
  description: string;
  steps: HowToStep[];
}

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  schemaData?: object;
  keywords?: string;
  noindex?: boolean;
  breadcrumbs?: { name: string; item: string }[];
  faq?: { question: string; answer: string }[];
  howTo?: HowToSchema;
}

const SEO: React.FC<SEOProps> = ({
  title = "AI Automation Agency UK & USA | Emerge Automations",
  description = "Emerge Automations helps SMEs in the UK and USA scale with custom AI voice agents, n8n workflow automation, and strategic AI consulting. Transform your business with rapid AI deployment.",
  canonical = "https://emergeautomations.com",
  ogType = "website",
  ogImage = "https://emergeautomations.com/og-image.png",
  schemaData,
  keywords = "AI automation agency, custom AI voice agents, n8n automation, AI consulting UK, AI consulting USA, business process automation, AI workflow optimization, SME AI solutions",
  noindex = false,
  breadcrumbs,
  faq,
  howTo
}) => {
  const fullTitle = title.includes("Emerge Automations") ? title : `${title} | Emerge Automations`;

  // Fallback to manually set document.title to ensure it's updated before prerender captures
  useEffect(() => {
    console.log('SEO.tsx useEffect running. fullTitle:', fullTitle);
    document.title = fullTitle;
    console.log('document.title is now:', document.title);
  }, [fullTitle]);

  // Default Organization Schema
  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Emerge Automations",
    "url": "https://emergeautomations.com",
    "logo": "https://emergeautomations.com/logo.png",
    "description": description,
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "UK"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "emergeautomations@gmail.com",
      "contactType": "customer service"
    },
    "sameAs": [
      "https://twitter.com/emergeautomations",
      "https://linkedin.com/company/emerge-automations"
    ]
  };

  const schemas: any[] = [schemaData || defaultSchema];

  if (breadcrumbs) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": crumb.item
      }))
    });
  }

  if (faq) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faq.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    });
  }

  if (howTo) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": howTo.name,
      "description": howTo.description,
      "step": howTo.steps.map((step, index) => ({
        "@type": "HowToStep",
        "position": index + 1,
        "name": step.name,
        "text": step.text
      }))
    });
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical} />
      {!noindex ? (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      ) : (
        <meta name="robots" content="noindex, nofollow" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content="Emerge Automations" />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@emergeautomations" />
      <meta name="twitter:creator" content="@emergeautomations" />

      {/* Additional SEO Tags */}
      <meta name="author" content="Emerge Automations" />
      <meta name="theme-color" content="#0F172A" />

      {/* Hreflang Tags */}
      <link rel="alternate" hrefLang="en-gb" href={canonical} />
      <link rel="alternate" hrefLang="en-us" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Structured Data (JSON-LD) */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
